import type { ProjectDocument, ProjectSnapshot } from './project.js';

/**
 * Snapshot-based undo (design document D5).
 *
 * Command-and-inverse is tempting, but the perpend re-coordination operation
 * is a whole-wall coordinate transform whose inverse is not simply the reverse
 * gap change, and getting that wrong corrupts walls silently.
 *
 * Because bricks are immutable, a snapshot is an array of shared references
 * rather than a deep clone, so the budgets can be far higher than the
 * specification's.
 */
export const HISTORY_LIMIT = 200;
export const HISTORY_RECORD_BUDGET = 2_000_000;

function recordCount(snapshot: ProjectSnapshot): number {
  let total = 0;
  for (const wall of snapshot.walls) total += wall.geometry.bricks.length;
  return total;
}

function trim(stack: ProjectSnapshot[]): void {
  while (stack.length > HISTORY_LIMIT) stack.shift();
  let total = 0;
  for (const snapshot of stack) total += recordCount(snapshot);
  while (stack.length > 1 && total > HISTORY_RECORD_BUDGET) {
    total -= recordCount(stack[0] as ProjectSnapshot);
    stack.shift();
  }
}

export class HistoryStore {
  #undo: ProjectSnapshot[] = [];
  #redo: ProjectSnapshot[] = [];

  get canUndo(): boolean {
    return this.#undo.length > 0;
  }

  get canRedo(): boolean {
    return this.#redo.length > 0;
  }

  get depth(): number {
    return this.#undo.length;
  }

  clear(): void {
    this.#undo = [];
    this.#redo = [];
  }

  /** Record the state BEFORE a mutation, and drop any redo branch. */
  checkpoint(project: ProjectDocument): void {
    this.#undo.push(project.snapshot());
    trim(this.#undo);
    this.#redo = [];
  }

  /** Discard the most recent checkpoint, for a gesture that changed nothing. */
  abandon(): void {
    this.#undo.pop();
  }

  undo(project: ProjectDocument): boolean {
    const snapshot = this.#undo.pop();
    if (snapshot === undefined) return false;
    this.#redo.push(project.snapshot());
    trim(this.#redo);
    project.restore(snapshot);
    return true;
  }

  redo(project: ProjectDocument): boolean {
    const snapshot = this.#redo.pop();
    if (snapshot === undefined) return false;
    this.#undo.push(project.snapshot());
    trim(this.#undo);
    project.restore(snapshot);
    return true;
  }
}
